import "@/styles/estimate.css";
import useFormFeedback from "@/lib/useFormFeedback";
import { compressImageFile, estimateFailureMessage } from '../lib/estimateSubmit';
import React, { useState, useEffect, useRef } from 'react';
import { travelQuote } from '../lib/travel';
import catalog from '../data/bookingCatalog.json';
import { getHoliday, localDate, submissionId, finishSubmission } from '../lib/booking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://junk-in-da-truck-production.up.railway.app';
const API_BASE = `${BACKEND_URL}/api`;
const PHONE_DISPLAY = '(772) 494-5048';
const PHONE_TEL = 'tel:7724945048';
const SITE_URL = 'https://junkindatruck.com';

const TIME_WINDOWS = [
  '08:00 AM - 10:00 AM','10:00 AM - 12:00 PM','12:00 PM - 02:00 PM',
  '02:00 PM - 04:00 PM','04:00 PM - 06:00 PM'
];

const HOLIDAY_SURCHARGE = 250;
const ADDON_OPTIONS = catalog.addons;
const JOB_CATEGORIES = catalog.categories;

const getJobPrice = (label) => {
  for(const cat of JOB_CATEGORIES){
    const item = cat.items.find(i=>i.label===label);
    if(item) return item.price;
  }
  return 0;
};

const CONTACT_METHODS = ['Phone','Email','Text Message'];

const safeFetch = async (url, options) => {
  try {
    return await fetch(url, options);
  } catch {
    return null;
  }
};

const fetchPromoCodes = async () => {
  const res = await safeFetch(`${API_BASE}/promo-codes`);
  if (res?.ok) {
    return res.json();
  }

  return [];
};

const applyPromo = (code, codes, subtotal) => {
  const match = codes.find(c => c.code === code.toUpperCase().trim());
  if (!match) return null;
  const discount = match.discount_type === 'percent'
    ? Math.round(subtotal * match.discount_value / 100)
    : match.discount_value;
  return { ...match, discount };
};

const formatDateLong = (dateStr) => {
  if (!dateStr) return 'TBD';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
};

const processSteps = [
  {
    step: '01',
    title: 'Tell us about the job',
    copy: 'Share your job details, photos, and preferred schedule so we can price it accurately.',
  },
  {
    step: '02',
    title: 'We confirm the game plan',
    copy: 'We review your request, confirm the scope and price, and agree on an available time with you.',
  },
  {
    step: '03',
    title: 'Our crew gets to work',
    copy: 'Once the details are confirmed, our crew handles the lifting, hauling or delivery you requested.',
  },
];

const processSummary = processSteps
  .map((item) => `${item.step}. ${item.title} — ${item.copy}`)
  .join(' ');

export default function EstimateFormNative({ preferredCategory }) {
  const jobCategories = preferredCategory
    ? [...JOB_CATEGORIES].sort((a, b) => Number(b.category === preferredCategory) - Number(a.category === preferredCategory))
    : JOB_CATEGORIES;
  const [submitting,setSubmitting] = useState(false);
  const [submitted,setSubmitted]   = useState(false);
  const { setError, fieldProps, fieldError, errorSummary } = useFormFeedback("estimate");
  const [photos,setPhotos] = useState([]);
  const photoInputRef = useRef(null);
  const photosRef = useRef([]);
  const readingPhotos = useRef(false);
  const readError = useRef('');
  const [photoReads, setPhotoReads] = useState(false);
  const [photoError, setPhotoError] = useState('');

  const [promoCodes, setPromoCodes]   = useState([]);
  const [promoInput, setPromoInput]   = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  useEffect(() => {
    fetchPromoCodes().then(setPromoCodes).catch(() => {});
  }, []);

  const [form,setForm] = useState({
    name:'', email:'', address:'', phone:'',
    contactMethods:[],
    jobType:'', jobDescription:'',
    addons:[], addonQuantities:{},
    date:'', timeWindow:'',
    allowEarlyArrival: false,
    isBusiness: false,
    requestW9: false,
    requestCOI: false,
    paymentTerms: '',
    agreePrivacy: false,
    agreeLatePolicy: false,
    agreeTerms: false,
  });

  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const toggle = (k) => setForm(p=>({...p,[k]:!p[k]}));
  const toggleArr = (k,v) => setForm(p=>({
    ...p,[k]: p[k].includes(v) ? p[k].filter(x=>x!==v) : [...p[k],v]
  }));

  const isCustom = form.jobType === 'Custom Offer - Fill out description';
  const holidayName = getHoliday(form.date);
  const isHoliday = !!holidayName;
  const getAddonQuantity = (label) => {
    const quantity = Number(form.addonQuantities[label] ?? 1);
    return Number.isSafeInteger(quantity) && quantity > 0 ? quantity : 1;
  };
  const addonQuantities = Object.fromEntries(
    ADDON_OPTIONS.filter(opt => opt.quantityLabel && form.addons.includes(opt.label))
      .map(opt => [opt.label, getAddonQuantity(opt.label)])
  );
  const quantityNotes = Object.entries(addonQuantities).map(([label, count]) => `${label}: ${count}`).join(', ');
  const jobDescription = [form.jobDescription, quantityNotes ? `Add-on quantities: ${quantityNotes}` : ''].filter(Boolean).join('\n');
  const addonsTotal = form.addons.reduce((total, label) => {
    const option = ADDON_OPTIONS.find(opt => opt.label === label);
    return total + (option?.price || 0) * (option?.quantityLabel ? getAddonQuantity(label) : 1);
  }, 0);
  const travel = travelQuote(form.address, form.jobType);
  const preDiscount = (form.jobType ? getJobPrice(form.jobType) : 0) + addonsTotal + travel.fee + (isHoliday ? HOLIDAY_SURCHARGE : 0);
  const promoDiscount = (promoResult && promoResult !== 'invalid')
    ? (promoResult.discount_type === 'percent' ? Math.round(preDiscount * promoResult.discount_value / 100) : promoResult.discount_value)
    : 0;
  const formTotal = Math.max(0, preDiscount - promoDiscount);
  const today = localDate();

  const submitLabel = 'REQUEST ESTIMATE';
  const nextSteps = 'We’ll review your request and preferred date/time, then contact you to confirm pricing and availability. Your job is not booked until we confirm it with you.';


  const handlePhotoAdd = async e => {
    if (readingPhotos.current) return;
    const files = Array.from(e.target.files).slice(0, 6 - photosRef.current.length);
    e.target.value = '';
    const fail = message => {readError.current = message; setPhotoError(message);};
    if (files.some(f => (f.type && !f.type.startsWith('image/')) || f.size > 20*1024*1024)) {
      fail('Choose image files up to 20 MB each.'); return;
    }
    readingPhotos.current = true; setPhotoReads(true); readError.current = ''; setPhotoError('');
    try {
      const results = await Promise.allSettled(files.map(async file => {
        const photo = await compressImageFile(file);
        if (photo.dataUrl.length > 4*1024*1024) throw new Error('Processed photo exceeds 3 MB');
        return photo;
      }));
      const loaded = results.filter(r=>r.status==='fulfilled').map(r=>r.value);
      photosRef.current = [...photosRef.current,...loaded].slice(0,6); setPhotos(photosRef.current);
      if (results.some(r=>r.status==='rejected')) fail('Some photos could not be read. Choose them again or continue without the failed photos.');
    } finally {readingPhotos.current = false; setPhotoReads(false);}
  };
  const removePhoto = idx => {
    photosRef.current = photosRef.current.filter((_,i)=>i!==idx); setPhotos(photosRef.current);
  };

  const submit = async () => {
    if (readingPhotos.current) {setError('Please wait for your photos to finish loading.'); return;}
    if (readError.current) {setError(readError.current); return;}
    if(!form.name.trim())        { setError('Please enter your full name.', 'estimate-field-1'); return; }
    if(!form.email.trim())       { setError('Please enter your email address.', 'estimate-field-2'); return; }
    if(!form.address.trim())     { setError('Please enter your service address.', 'estimate-field-3'); return; }
    if(!form.phone.trim()) { setError('Please enter the best phone number for your quote.', 'estimate-field-4'); return; }
    if(form.contactMethods.length === 0) { setError('Please choose at least one preferred contact method.', 'estimate-contact-Phone'); return; }
    if(!form.jobType)     { setError('Please select a service type.', 'estimate-field-7'); return; }
    const invalidQuantity = ADDON_OPTIONS.find(opt => opt.quantityLabel && form.addons.includes(opt.label) &&
      (!Number.isSafeInteger(Number(form.addonQuantities[opt.label] ?? 1)) || Number(form.addonQuantities[opt.label] ?? 1) < 1 || Number(form.addonQuantities[opt.label] ?? 1) > 1000));
    if(invalidQuantity) { setError(`Please enter a whole number of 1 or more for ${invalidQuantity.label.toLowerCase()}.`, `quantity-${invalidQuantity.label.replace(/\s+/g, '-').toLowerCase()}`); return; }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    if(!emailOk){ setError('Please enter a valid email address.', 'estimate-field-2'); return; }
    const phoneDigits = form.phone.replace(/\D/g, '');
    if(phoneDigits.length < 10){ setError('Please enter a valid phone number.', 'estimate-field-4'); return; }
    if(!form.date){ setError('Please select a preferred date.', 'estimate-field-5'); return; }
    if(form.date < today){ setError('Please select a current service date.', 'estimate-field-5'); return; }
    if(!form.timeWindow){ setError('Please select an arrival window.', 'estimate-field-6'); return; }
    if(isCustom && !form.jobDescription.trim()){ setError('Please describe the custom job so we can prepare your estimate.', 'additional-notes'); return; }
    if(!form.agreePrivacy){ setError('You must acknowledge the Privacy Policy to continue.', 'estimate-privacy'); return; }
    if(!form.agreeLatePolicy){ setError('You must acknowledge the Late Payment Policy to continue.', 'estimate-late-policy'); return; }
    if(!form.agreeTerms){ setError('You must agree to the Terms and Conditions to continue.', 'estimate-terms'); return; }

    setSubmitting(true);
    setError('');

    try {
      const res = await safeFetch(`${API_BASE}/estimate-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submission_id: submissionId(),
          name: form.name,
          email: form.email,
          address: form.address,
          phone: form.phone,
          contact_methods: form.contactMethods,
          job_type: form.jobType,
          job_description: jobDescription,
          addons: form.addons,
          addon_quantities: addonQuantities,
          date: form.date,
          time_window: form.timeWindow,
          allow_early_arrival: form.allowEarlyArrival,
          is_business: form.isBusiness,
          request_w9: form.requestW9,
          request_coi: form.requestCOI,
          payment_terms: form.paymentTerms,
          agree_privacy: form.agreePrivacy,
          agree_late_policy: form.agreeLatePolicy,
          agree_terms: form.agreeTerms,
          photos: photosRef.current,
          estimated_total: formTotal,
          is_custom: isCustom,
          promo_code: promoResult && promoResult !== 'invalid' ? promoResult.code : null,
          promo_discount: promoDiscount > 0 ? promoDiscount : 0,
          holiday_name: holidayName || null,
          holiday_surcharge: isHoliday ? HOLIDAY_SURCHARGE : 0,
        }),
      });

      const data = res ? await res.json().catch(() => ({})) : {};
      if (!res?.ok || data.success !== true) {
        throw new Error(estimateFailureMessage(data.detail || data.message || 'We could not confirm your request. Retry this same form to avoid a duplicate request.'));
      }
      finishSubmission(); setSubmitted(true);
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please call or text us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if(submitted) return (
    <div className="estimate-page success-page">

      <div className="success-shell">
        <div className="success-card">
          <div className="success-badge">Request received</div>
          <h1>Your estimate request has been received.</h1>
          <p>
            Thanks, {form.name.split(' ')[0] || 'there'}. We’ve saved your preferred date and time. We’ll contact you to confirm pricing, scope and availability before your job is booked.
          </p>
          <div className="success-summary">
            <div><span>Service</span><strong>{form.jobType}</strong></div>
            <div><span>Date</span><strong>{formatDateLong(form.date)}</strong></div>
            <div><span>Window</span><strong>{form.timeWindow}</strong></div>
            <div><span>{travel.pending ? 'Estimate before travel quote' : 'Estimated total'}</span><strong>{isCustom ? 'TBD' : `$${formTotal}`}</strong></div>
          </div>
          <div className="success-actions">
            <a className="primary-btn" href={PHONE_TEL}>Call {PHONE_DISPLAY}</a>
            <a className="secondary-btn" href={SITE_URL}>Back to main site</a>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="estimate-page">


      <div className="form-section form-section-centered">
        <div className="section-shell form-layout form-layout-centered">
          <div className="form-column form-column-centered">
            <div className="form-stack">
              <div className="inline-form-intro">
                <h2>Tell us about the job.</h2>
                <div className="form-subtitle-row">
                  <p>
                    Share the details and photos so we can prepare your estimate
                  </p>
                  <button
                    type="button"
                    className="info-trigger"
                    aria-label="How it works"
                    title={processSummary}
                    onClick={() => setShowHowItWorks((v) => !v)}
                  >
                    i
                  </button>
                </div>
                <div className="trust-strip" aria-label="estimate trust signals">
                  <div className="trust-pill">Fast follow-up by call, text, or email</div>
                  <div className="trust-pill">Licensed & insured</div>
                  <div className="trust-pill">Photo uploads help us quote faster</div>
                </div>
                {showHowItWorks && (
                  <div className="how-it-works-popover">
                    <div className="how-it-works-heading">How it works</div>
                    <div className="how-it-works-list">
                      {processSteps.map((item) => (
                        <div key={item.step} className="how-it-works-item">
                          <strong>{item.step}. {item.title}</strong>
                          <span>{item.copy}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <section className="form-card">
                <div className="card-title-row">
                  <span className="card-step">01</span>
                  <div>
                    <h3>Contact information</h3>
                    <p>How we reach you and where the work happens.</p>
                  </div>
                </div>
                <div className="field-grid two-col">
                  <div className="field-block">
                    <label htmlFor="estimate-field-1">Full Name *</label>
                    <input {...fieldProps("estimate-field-1")} required aria-required="true" value={form.name} onChange={e=>set('name',e.target.value)} placeholder="Full Name" />
                    {fieldError("estimate-field-1")}
                  </div>
                  <div className="field-block">
                    <label htmlFor="estimate-field-2">Email *</label>
                    <input {...fieldProps("estimate-field-2")} required aria-required="true" type="email" value={form.email} onChange={e=>set('email',e.target.value)} placeholder="Email Address" />
                    {fieldError("estimate-field-2")}
                  </div>
                  <div className="field-block field-span-full">
                    <label htmlFor="estimate-field-3">Service Address *</label>
                    <input {...fieldProps("estimate-field-3")} required aria-required="true" value={form.address} onChange={e=>set('address',e.target.value)} placeholder="Street address, city, ZIP" />
                    {fieldError("estimate-field-3")}
                  </div>
                  <div className="field-block">
                    <label htmlFor="estimate-field-4">Phone *</label>
                    <input {...fieldProps("estimate-field-4")} required aria-required="true" type="tel" value={form.phone} onChange={e=>set('phone',e.target.value)} placeholder="Best phone number for quote follow-up" />
                    {fieldError("estimate-field-4")}
                    <small className="field-help">We use this to confirm pricing, timing, and any job details fast.</small>
                  </div>
                  <div className="field-block">
                    <div id="contact-method-label">Preferred contact method *</div>
                    <div className="chip-row" role="group" aria-labelledby="contact-method-label">
                      {CONTACT_METHODS.map((m)=> (
                        <button
                          key={m}
                          {...fieldProps(`estimate-contact-${m}`)}
                          type="button"
                          aria-pressed={form.contactMethods.includes(m)}
                          className={`chip ${form.contactMethods.includes(m) ? 'active' : ''}`}
                          onClick={()=>toggleArr('contactMethods',m)}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                    <small className="field-help">Pick at least one so we know how to reach you first.</small>
                    {fieldError("estimate-contact-Phone")}
                  </div>
                </div>
              </section>

              <section className="form-card">
                <div className="card-title-row">
                  <span className="card-step">02</span>
                  <div>
                    <h3>Job details</h3>
                    <p>Scope, service type, add-ons, and photos for cleaner pricing.</p>
                  </div>
                </div>
                <div className="field-grid two-col">
                  <div className="field-block field-span-full">
                    <label htmlFor="estimate-field-7">Service Required *</label>
                    <select {...fieldProps("estimate-field-7")} required aria-required="true" value={form.jobType} onChange={e=>set('jobType',e.target.value)}>
                      <option value="">Select a service…</option>
                      {jobCategories.map(cat=> (
                        <optgroup key={cat.category} label={cat.category}>
                          {cat.items.map(item => (
                            <option key={item.label} value={item.label}>
                              {item.label}{item.displayPrice ? ` (${item.displayPrice})` : item.price>0?` ($${item.price.toLocaleString('en-US')})`:item.price===0&&!item.label.includes('Custom')?' (FREE)':''}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    {fieldError("estimate-field-7")}
                    <p className="field-help">Not sure which load size or labor duration fits? Choose “Custom Offer” and add photos. We will confirm the space, crew and time needed with you.</p>
                  </div>

                  <div className="field-block field-span-full">
                    <label>Add-ons</label>
                    <p className="field-help">Propane tanks are a free add-on to a service. Paint/chemical handling carries the listed fee; tell us the type and quantity so our crew can confirm the scope. We do not accept medical or biological waste. Other exclusions are listed in our Terms.</p>
                    <div className="addon-grid">
                      {ADDON_OPTIONS.map(opt=> (
                        <div key={opt.label} className={`addon-item ${form.addons.includes(opt.label) ? 'active' : ''}`}>
                          <label className="addon-check">
                            <input type="checkbox" aria-label={opt.label} checked={form.addons.includes(opt.label)} onChange={()=>toggleArr('addons',opt.label)} />
                          </label>
                          <button
                            type="button"
                            className="addon-content"
                            aria-pressed={form.addons.includes(opt.label)}
                            onClick={()=>toggleArr('addons',opt.label)}
                          >
                            <span>{opt.label}</span>
                            <strong>{opt.price === 0 ? opt.displayPrice : `+${opt.displayPrice}`}</strong>
                          </button>
                          {opt.quantityLabel && form.addons.includes(opt.label) && (
                            <div className="addon-quantity">
                              <label htmlFor={`quantity-${opt.label.replace(/\s+/g, '-').toLowerCase()}`}>{opt.quantityLabel}</label>
                              <input
                                {...fieldProps(`quantity-${opt.label.replace(/\s+/g, '-').toLowerCase()}`)}
                                type="number" min="1" step="1" inputMode="numeric"
                                max="1000" value={form.addonQuantities[opt.label] ?? 1}
                                onChange={e => setForm(current => ({...current, addonQuantities: {...current.addonQuantities, [opt.label]: e.target.value}}))}
                              />
                              <strong>${opt.price * getAddonQuantity(opt.label)}</strong>
                              {fieldError(`quantity-${opt.label.replace(/\s+/g, '-').toLowerCase()}`)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="field-block field-span-full">
                    <label htmlFor="additional-notes">Additional Notes {isCustom ? '*' : ''}</label>
                    <textarea {...fieldProps("additional-notes")} required={isCustom} aria-required={isCustom}
                      value={form.jobDescription}
                      onChange={e=>set('jobDescription',e.target.value)}
                      placeholder={isCustom ? 'Describe the custom job, materials, volume, access, and anything that affects pricing…' : 'Any extra notes you’d like to share? You can leave this blank.'}
                    />
                    {fieldError("additional-notes")}
                  </div>
                </div>

                <div className="upload-section">
                  <div className="upload-header">
                    <div>
                      <h4>Photos</h4>
                      <p>Upload up to 6 images so we can quote the job with less guesswork.</p>
                    </div>
                    <button type="button" className="secondary-btn" disabled={photoReads || photos.length >= 6} onClick={()=>photoInputRef.current?.click()}>
                      {photoReads ? 'Processing images...' : photos.length >= 6 ? '6 photos added' : photos.length ? 'Add more photos' : 'Add photos'}
                    </button>
                  </div>
                  <p className="field-help" role="status">{photos.length} of 6 photos added. Photos are optional.</p>
                  <input ref={photoInputRef} disabled={photoReads} type="file" accept="image/*" multiple onChange={handlePhotoAdd} style={{display:'none'}} />
                  {photos.length > 0 && <div className="photo-grid">
                    {photos.map((photo, index) => (
                      <div key={index} className="photo-tile filled">
                        <img src={photo.dataUrl} alt={photo.name} />
                        <button type="button" className="photo-remove" aria-label={`Remove photo ${photo.name}`} onClick={()=>removePhoto(index)}>×</button>
                        <div className="photo-caption">{photo.name}</div>
                      </div>
                    ))}
                  </div>}
                </div>
              </section>

              <section className="form-card">
                <div className="card-title-row">
                  <span className="card-step">03</span>
                  <div>
                    <h3>Schedule preferences</h3>
                    <p>Tell us when you would prefer the job done. We will confirm availability with you.</p>
                  </div>
                </div>
                <div className="field-grid two-col">
                  <div className="field-block">
                    <label htmlFor="estimate-field-5">Preferred Date *</label>
                    <input {...fieldProps("estimate-field-5")} required aria-required="true" type="date" min={today} value={form.date} onChange={e=>{set('date',e.target.value);set('timeWindow','');}} />
                    {fieldError("estimate-field-5")}
                  </div>
                  <div className="field-block">
                    <label htmlFor="estimate-field-6">Preferred Time Window *</label>
                    <select {...fieldProps("estimate-field-6")} required aria-required="true" value={form.timeWindow} onChange={e=>set('timeWindow',e.target.value)} disabled={!form.date}>
                      {!form.date
                        ? <option value="">Select a date first…</option>
                        : <>
                            <option value="">Choose a window…</option>
                            {TIME_WINDOWS.map(w=><option key={w}>{w}</option>)}
                          </>
                      }
                    </select>
                    {fieldError("estimate-field-6")}
                  </div>
                </div>
                <label className="checkbox-row">
                  <input type="checkbox" checked={form.allowEarlyArrival} onChange={()=>toggle('allowEarlyArrival')} />
                  <span>We can arrive earlier if the route opens up.</span>
                </label>
                {isHoliday && (
                  <div className="notice warning">
                    <strong>{holidayName}</strong> has a holiday service surcharge of ${HOLIDAY_SURCHARGE}.
                  </div>
                )}
              </section>

              <section className="form-card">
                <div className="card-title-row">
                  <span className="card-step">04</span>
                  <div>
                    <h3>Business requests</h3>
                    <p>Optional fields for property managers, investors, and commercial work.</p>
                  </div>
                </div>
                <label className="checkbox-row">
                  <input type="checkbox" checked={form.isBusiness} onChange={()=>toggle('isBusiness')} />
                  <span>This estimate is for a business or commercial property.</span>
                </label>

                {form.isBusiness && (
                  <div className="business-panel">
                    <label className="checkbox-row">
                      <input type="checkbox" checked={form.requestW9} onChange={()=>toggle('requestW9')} />
                      <span>Request W-9</span>
                    </label>
                    <label className="checkbox-row">
                      <input type="checkbox" checked={form.requestCOI} onChange={()=>toggle('requestCOI')} />
                      <span>Request Certificate of Insurance (COI)</span>
                    </label>
                    <div className="field-block compact">
                      <div id="payment-terms-label">Payment Terms</div>
                      <div className="chip-row" role="group" aria-labelledby="payment-terms-label">
                        {['Net 15','Net 30'].map(t=> (
                          <button
                            key={t}
                            type="button"
                            aria-pressed={form.paymentTerms===t}
                            className={`chip ${form.paymentTerms===t?'active':''}`}
                            onClick={()=>set('paymentTerms', form.paymentTerms===t?'':t)}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </section>

              <section className="form-card">
                <div className="card-title-row">
                  <span className="card-step">05</span>
                  <div>
                    <h3>Consent & legal</h3>
                    <p>Required confirmations before we can process the estimate.</p>
                  </div>
                </div>
                <div className="legal-stack">
                  <label className="checkbox-row legal-row required">
                    <input {...fieldProps("estimate-privacy")} type="checkbox" required aria-required="true" checked={form.agreePrivacy} onChange={()=>toggle('agreePrivacy')} />
                    <span>
                      I have read and understand the <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>. *
                    </span>
                  </label>
                  {fieldError("estimate-privacy")}
                  <label className="checkbox-row legal-row required">
                    <input {...fieldProps("estimate-late-policy")} type="checkbox" required aria-required="true" checked={form.agreeLatePolicy} onChange={()=>toggle('agreeLatePolicy')} />
                    <span>
                      I have read and understand the <a href="/late-policy" target="_blank" rel="noreferrer">Late Payment Policy</a>. *
                    </span>
                  </label>
                  {fieldError("estimate-late-policy")}
                  <label className="checkbox-row legal-row required">
                    <input {...fieldProps("estimate-terms")} type="checkbox" required aria-required="true" checked={form.agreeTerms} onChange={()=>toggle('agreeTerms')} />
                    <span>
                      I agree to the <a href="/terms" target="_blank" rel="noreferrer">Terms and Conditions</a> and understand this request may be followed up by phone, text, or email. *
                    </span>
                  </label>
                  {fieldError("estimate-terms")}
                  <p className="field-help">The Terms also describe occasional marketing messages and project photos/video when you hire us. Marketing messages include opt-out instructions. To keep your project out of promotional media, tell us in writing or tell the crew before work begins. <a href="/terms#communications-and-media" target="_blank" rel="noreferrer">Read the communication and media details ↗</a></p>
                </div>
              </section>
              <div className="summary-stack summary-stack-bottom">
                <div className="summary-card">
                  <div className="summary-card-top">
                    <span>Estimate summary</span>
                    <h3>{form.jobType || 'Select a service'}</h3>
                  </div>

                  <div className="promo-box">
                    <label htmlFor="promo-code">Referral / Club Code</label>
                    <div className="promo-row">
                      <input id="promo-code"
                        value={promoInput}
                        onChange={e => {
                          const val = e.target.value.toUpperCase();
                          setPromoInput(val);
                          if (!val.trim()) { setPromoResult(null); return; }
                          const result = applyPromo(val, promoCodes, preDiscount);
                          setPromoResult(result || 'invalid');
                        }}
                        placeholder="ENTER CODE"
                        className={promoResult === 'invalid' ? 'invalid' : promoResult ? 'valid' : ''}
                      />
                      {promoResult && promoResult !== 'invalid' && (
                        <button type="button" className="clear-btn" onClick={() => { setPromoInput(''); setPromoResult(null); }}>
                          Remove
                        </button>
                      )}
                    </div>
                    {promoResult === 'invalid' && <p className="promo-state error">Code not recognized</p>}
                    {promoResult && promoResult !== 'invalid' && (
                      <p className="promo-state success">
                        {promoResult.label || promoResult.code} applied — save ${promoDiscount}
                      </p>
                    )}
                  </div>

                  <div className="summary-breakdown">
                    <div><span>Base service</span><strong>{form.jobType ? (isCustom ? 'TBD' : `$${getJobPrice(form.jobType)}`) : '$0'}</strong></div>
                    <div><span>Add-ons</span><strong>{addonsTotal ? `$${addonsTotal}` : '$0'}</strong></div>
                    {quantityNotes && <p className="field-help">{quantityNotes}</p>}
                    {isHoliday && <div><span>Holiday surcharge</span><strong>${HOLIDAY_SURCHARGE}</strong></div>}
                    {promoDiscount > 0 && <div className="discount-row"><span>Discount</span><strong>- ${promoDiscount}</strong></div>}
                  </div>

                  {travel.applies && <p role="status">{travel.pending
                    ? 'Travel fee pending: enter a full address with city and FL. Unrecognized areas require a travel quote before booking is confirmed.'
                    : `Zone ${travel.zone} travel fee: $${travel.fee} (included below).`}</p>}
                  <div className="summary-total">
                    <span>{travel.pending ? 'Estimate before travel quote' : 'Estimated total'}</span>
                    <strong>{!form.jobType ? '$0' : isCustom ? 'TBD' : `$${formTotal}`}</strong>
                  </div>

                  {errorSummary}

                  <div className="summary-reassurance">
                    <div><strong>What happens next:</strong> {nextSteps} We’ll use your selected contact method.</div>
                    <div><strong>Need it faster?</strong> Call or text <a href={PHONE_TEL}>{PHONE_DISPLAY}</a>.</div>
                  </div>

                  {photoError && <div role="alert"><p>{photoError}</p><button type="button" onClick={()=>{readError.current='';setPhotoError('');setError('');}}>Continue without failed photos</button></div>}
                  <button className="primary-btn submit-btn" onClick={submit} disabled={submitting || photoReads}>
                    {photoReads ? 'Reading photos...' : submitting ? 'Submitting...' : submitLabel}
                  </button>

                  <p className="summary-footnote">
                    Recognized junk-removal city zones include the travel fee automatically. Other addresses require a travel quote before confirmation. Delivery prices already include their selected zone. Final pricing may also change with the actual volume, labor, or materials.
                  </p>
                </div>

                <div className="contact-card">
                  <span>Need help fast?</span>
                  <h4>Call or text the crew directly.</h4>
                  <a href={PHONE_TEL}>{PHONE_DISPLAY}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
